// ** Util Import
import { hexToRGBA } from '@erikdakoda/ui/shared/colorUtils';

// ** Components Import
import AppBarContent from './components/horizontal/app-bar-content';
import Navigation from './components/horizontal/navigation';
import Footer from './components/shared-components/footer';
import Background from './components/Background';

// ** Theme Config Import
import themeConfig from '../config';
import { type Theme } from '@mui/material/styles';

// ** Type Import
import type { LayoutProps } from '../types';
import MuiAppBar, { type AppBarProps } from '@mui/material/AppBar';
import Box, { type BoxProps } from '@mui/material/Box';
import MuiToolbar, { type ToolbarProps } from '@mui/material/Toolbar';
import { styled } from '@mui/material/styles';

const HorizontalLayoutWrapper = styled('div')({
  height: '100%',
  // When ancestors lack an explicit height, `height: 100%` can compute to 0. Keep a viewport
  // floor so `overflow: clip` does not hide page content during that window (and for e2e).
  minHeight: '100vh',
  display: 'flex',
  ...(themeConfig.config.horizontalMenuAnimation && { overflow: 'clip' }),
});

const MainContentWrapper = styled(Box)<BoxProps>({
  flexGrow: 1,
  minWidth: 0,
  minHeight: '100vh',
  display: 'grid',
  gridTemplateRows: 'min-content 1fr min-content',
});

const Toolbar = styled(MuiToolbar)<ToolbarProps>(({ theme }) => ({
  width: '100%',
  padding: `${theme.spacing(0, 6)} !important`,
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(4),
  },
  [theme.breakpoints.down('xs')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  padding: theme.mixins.contentPadding(1),
  transition: 'padding .25s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.mixins.contentPadding(0.5),
  },
}));

const HorizontalLayout = (props: LayoutProps) => {
  // ** Props
  const {
    hidden,
    children,
    settings,
    footerProps,
    saveSettings,
    contentHeightFixed,
    noPadding,
    contentAlign,
    horizontalLayoutProps,
  } = props;

  // ** Vars
  const { skin, appBar, navHidden, navBelow, contentWidth } = settings;
  const appBarProps = horizontalLayoutProps?.appBar?.componentProps;
  const userNavMenuContent = horizontalLayoutProps?.navMenu?.content;

  let userAppBarStyle = {};
  if (appBarProps && appBarProps.sx) {
    userAppBarStyle = appBarProps.sx;
  }
  const userAppBarProps = Object.assign({}, appBarProps);
  delete userAppBarProps.sx;

  const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme }) => ({
    transition:
      'border-bottom 0.2s ease-in-out, backdrop-filter .25s ease-in-out, box-shadow .25s ease-in-out',
    ...(appBar === 'fixed'
      ? theme.palette.background.filter && {
          backdropFilter: theme.palette.background.filter,
          backgroundColor: (theme: Theme) => hexToRGBA(theme.palette.background.paper, 0.85),
        }
      : {}),
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 6),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    ...theme.mixins.toolbar,
    ...(appBar === 'static' && { zIndex: 13 }),
    ...(skin === 'bordered' && {
      borderBottom: `1px solid ${theme.palette.divider}`,
    }),
    ...userAppBarStyle,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    paddingTop: 'env(safe-area-inset-top)',
  }));

  return (
    <HorizontalLayoutWrapper className="layout-wrapper horizontal-layout-wrapper">
      <Background />
      <MainContentWrapper
        className="layout-content-wrapper"
        sx={{ ...(contentHeightFixed && { maxHeight: '100vh' }) }}
      >
        {/* Navbar (or AppBar) and Navigation Menu Wrapper */}
        <AppBar
          color="default"
          elevation={skin === 'bordered' ? 0 : 5}
          className="layout-navbar-and-nav-container"
          position={appBar === 'fixed' ? 'sticky' : 'static'}
          {...userAppBarProps}
        >
          {/* Navbar / AppBar */}
          <Box
            className="layout-navbar"
            sx={{
              width: '100%',
              ...(navHidden || !navBelow
                ? {}
                : {
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }),
            }}
          >
            <Toolbar
              className="navbar-content-container"
              sx={{
                mx: 'auto',
                ...(contentWidth === 'boxed' && {
                  '@media (min-width:1440px)': { maxWidth: 1440 },
                }),
              }}
            >
              <AppBarContent
                {...props}
                hidden={hidden}
                settings={settings}
                saveSettings={saveSettings}
                appBarContent={horizontalLayoutProps?.appBar?.content}
                appBarBranding={horizontalLayoutProps?.appBar?.branding}
                appBarNav={horizontalLayoutProps?.appBar?.nav}
              />
            </Toolbar>
          </Box>

          {/* Navigation Menu */}
          {navHidden || !navBelow ? null : (
            <Box
              className="layout-horizontal-nav"
              sx={{ width: '100%', ...horizontalLayoutProps?.navMenu?.sx }}
            >
              <Toolbar
                className="horizontal-nav-content-container"
                sx={{
                  mx: 'auto',
                  ...(contentWidth === 'boxed' && {
                    '@media (min-width:1440px)': { maxWidth: 1440 },
                  }),
                  minHeight: (theme) =>
                    skin === 'bordered'
                      ? `${(theme.mixins.toolbar.minHeight as number) - 1}px !important`
                      : null,
                }}
              >
                {(userNavMenuContent && userNavMenuContent(props)) || (
                  <Navigation
                    {...props}
                    horizontalNavItems={
                      (horizontalLayoutProps as NonNullable<LayoutProps['horizontalLayoutProps']>)
                        .navMenu?.navItems
                    }
                  />
                )}
              </Toolbar>
            </Box>
          )}
        </AppBar>

        {/* Content */}
        <ContentWrapper
          className="layout-page-content"
          sx={{
            ...(contentHeightFixed
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : { overflowX: 'hidden' }),
            ...(noPadding && { padding: 0 }),
            ...(contentAlign && { alignItems: contentAlign, '& > div': { maxWidth: '100%' } }),
            ...(contentWidth === 'boxed' && {
              mx: 'auto',
              '@media (min-width:1440px)': { maxWidth: 1440 },
              '@media (min-width:1200px)': { maxWidth: '100%' },
            }),
          }}
        >
          {children}
        </ContentWrapper>

        {/* Footer */}
        <Footer {...props} footerStyles={footerProps?.sx} footerContent={footerProps?.content} />
      </MainContentWrapper>
    </HorizontalLayoutWrapper>
  );
};

export default HorizontalLayout;
